import React from "react";
import {
  FaArrowRight,
  FaChalkboardTeacher,
  FaClock,
  FaUsers,
} from "react-icons/fa";

import {
  cn,
  fillPercentage,
  getHexColor,
  horairesOf,
  isDark,
  isGroupFull,
  lighten,
  teachersOf,
} from "./groupPlanningUtils";

export default function GroupMiniCard({ group, dojoName, onOpen }) {
  const baseColor = getHexColor(group.color_groupe);
  const lightColor = lighten(baseColor, 0.68);
  const titleColor = isDark(baseColor) ? "#fff" : "#0f172a";
  const horaires = horairesOf(group);
  const teachers = teachersOf(group);
  const full = isGroupFull(group);
  const percentage = fillPercentage(group);

  return (
    <article className="group relative h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
      <div
        className="relative p-5"
        style={{
          background: `linear-gradient(135deg, ${baseColor} 0%, ${lightColor} 100%)`,
        }}
      >
        <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/20 blur-2xl" />

        <div className="relative flex flex-wrap gap-2">
          <span className="rounded-full bg-white/65 px-3 py-1 text-[11px] font-bold text-slate-900 shadow-sm">
            {dojoName || "Dojo"}
          </span>

          {group.discipline_groupe && (
            <span className="rounded-full bg-white/65 px-3 py-1 text-[11px] font-bold text-slate-900 shadow-sm">
              {group.discipline_groupe}
            </span>
          )}

          {full && (
            <span className="rounded-full bg-rose-600 px-3 py-1 text-[11px] font-bold uppercase text-white shadow-sm">
              Complet
            </span>
          )}
        </div>

        <h4
          className="relative mt-5 font-barlow text-2xl font-extrabold uppercase leading-tight"
          style={{ color: titleColor }}
        >
          {group.nom}
        </h4>

        {group.annees_naissance && (
          <p
            className="relative mt-2 text-sm font-bold italic"
            style={{ color: titleColor }}
          >
            {group.annees_naissance}
          </p>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5 text-slate-900 dark:text-white">
        <div className="grid gap-3 text-sm">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <FaChalkboardTeacher />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                Enseignant(s)
              </p>
              <p className="mt-1 leading-6 text-slate-600 dark:text-slate-300">
                {teachers.length ? teachers.join(" • ") : "-"}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <FaClock />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                Horaires
              </p>

              {horaires.length ? (
                <ul className="mt-1 space-y-1 leading-6 text-slate-600 dark:text-slate-300">
                  {horaires.slice(0, 3).map((horaire, index) => (
                    <li key={index}>• {horaire}</li>
                  ))}

                  {horaires.length > 3 && (
                    <li className="text-xs italic text-slate-400">
                      … +{horaires.length - 3} autres créneaux
                    </li>
                  )}
                </ul>
              ) : (
                <p className="mt-1 italic text-slate-500">Non renseigné.</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
          <div className="flex items-center justify-between gap-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            <span className="inline-flex items-center gap-2">
              <FaUsers className="text-primary" />
              Effectif
            </span>
            <span>{percentage}%</span>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{
                width: `${Math.min(percentage, 100)}%`,
              }}
            />
          </div>

          <p className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-200">
            {group.adherentCount || 0}/{group.quota_groupe || 0} inscrit(s)
          </p>
        </div>

        <button
          type="button"
          onClick={() => onOpen(group)}
          className={cn(
            "mt-5 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold",
            "border border-primary/25 bg-primary/10 text-primary transition",
            "hover:-translate-y-0.5 hover:bg-primary hover:text-white"
          )}
        >
          Voir les détails
          <FaArrowRight className="text-xs" />
        </button>
      </div>

      <div
        aria-hidden
        className="h-1.5 w-full opacity-70 transition group-hover:opacity-100"
        style={{
          background: `linear-gradient(to right, transparent, ${baseColor}, transparent)`,
        }}
      />
    </article>
  );
}